"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { TONE_ACTIVE, useCalendarContext } from "../calendar-context";
import { type CalendarView } from "../types";

// =============================================================================

const VIEWS: CalendarView[] = ["week", "month"];

const NAV_BUTTON =
  "grid h-8 place-items-center text-muted-foreground transition-colors outline-none hover:bg-signal-tint hover:text-signal focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-inset";

type CalendarToolbarProps = {
  view: CalendarView;
  /** Null until hydrated: the period depends on the browser's time zone */
  title: string | null;
  onViewChange: (view: CalendarView) => void;
  onPrevious: () => void;
  onToday: () => void;
  onNext: () => void;
  headerActions?: ReactNode;
};

/** Period title, previous / today / next, the view switcher and the actions */
export function CalendarToolbar({
  view,
  title,
  onViewChange,
  onPrevious,
  onToday,
  onNext,
  headerActions,
}: CalendarToolbarProps) {
  const { labels, tone } = useCalendarContext();

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-line p-3 sm:px-4">
      <div
        aria-live="polite"
        className="w-full min-w-0 truncate text-base font-semibold tracking-tight text-foreground sm:w-auto sm:flex-1"
      >
        {title ?? (
          <Skeleton className="h-6 w-44 bg-surface-2 motion-reduce:animate-none" />
        )}
      </div>

      <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        {headerActions}

        <div className="flex divide-x divide-line overflow-hidden rounded-md border border-line">
          <button
            type="button"
            aria-label={labels.previous}
            onClick={onPrevious}
            className={cn(NAV_BUTTON, "w-8")}
          >
            <ChevronLeft aria-hidden className="size-4" />
          </button>
          <button
            type="button"
            onClick={onToday}
            className={cn(NAV_BUTTON, "px-3 text-xs font-medium")}
          >
            {labels.today}
          </button>
          <button
            type="button"
            aria-label={labels.next}
            onClick={onNext}
            className={cn(NAV_BUTTON, "w-8")}
          >
            <ChevronRight aria-hidden className="size-4" />
          </button>
        </div>

        {/* Segmented control, pushed to the right on phones */}
        <div className="ml-auto flex rounded-md border border-line bg-surface-2 p-0.5 sm:ml-0">
          {VIEWS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={option === view}
              onClick={() => onViewChange(option)}
              className={cn(
                "h-7 rounded-[calc(var(--radius-md)-2px)] px-3 text-xs font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                option === view
                  ? tone === "signal"
                    ? TONE_ACTIVE.signal
                    : "bg-background text-foreground shadow-xs"
                  : tone === "signal"
                    ? "text-muted-foreground hover:text-signal"
                    : "text-muted-foreground hover:text-foreground"
              )}
            >
              {labels[option]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
